// Mock data generators for LLM traces and evaluations
// This will eventually be replaced by real GraphQL queries

export type LlmTraceData = {
  id: string;
  parentSpanId: string | null;
  createdAt: string;
  modelName: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
  totalCost: string;
  latencyMs: number;
  functionId: string;
  metadata: {
    brandCode: string;
    operationType: string;
    userId: string;
    spanId: string;
    sessionId: string;
  };
  systemPrompt: string | null;
  inputMessages: Array<{ role: string; content: string }>;
  outputContent: any;
};

export type LlmEvaluationData = {
  id: string;
  createdAt: string;
  evaluatorModel: string;
  status: string;
  latencyMs: number | null;
  scores: {
    spanId: string;
    operationType: string;
    brandCode: string;
    traceId: string;
    functionId: string;
  };
  evaluationOutput: {
    overallScore: number;
    accuracy: {
      score: number;
      reasoning: string;
    };
    completeness: {
      score: number;
      reasoning: string;
    };
    instructionFollowing: {
      score: number;
      reasoning: string;
    };
    helpfulness?: {
      score: number;
      reasoning: string;
    };
    errorHandling?: {
      score: number;
      reasoning: string;
    };
    improvements: string[];
    summary: string;
  } | null;
  evaluationPrompt: string;
};

// UI-specific types for hierarchical display
export type TraceRow = LlmTraceData & {
  children?: LlmTraceData[];
  isChild?: boolean;
};

export const generateMockTraces = (): LlmTraceData[] => {
  const baseTraces = [
    {
      id: 'trace-1',
      parentSpanId: null,
      createdAt: '2024-01-15T10:30:00Z',
      modelName: 'gpt-4',
      status: 'completed',
      inputTokens: 150,
      outputTokens: 200,
      totalCost: '0.0045',
      latencyMs: 1200,
      functionId: 'chat-completion',
      metadata: {
        brandCode: 'ACME',
        operationType: 'chat',
        userId: 'user-123',
        spanId: 'span-1',
        sessionId: 'session-abc',
      },
      systemPrompt: 'You are a helpful assistant.',
      inputMessages: [{ role: 'user', content: 'What is the weather today?' }],
      outputContent: {
        role: 'assistant',
        content: 'I cannot access real-time weather data.',
      },
    },
    {
      id: 'trace-2',
      parentSpanId: 'trace-1',
      createdAt: '2024-01-15T10:30:05Z',
      modelName: 'gpt-3.5-turbo',
      status: 'completed',
      inputTokens: 80,
      outputTokens: 120,
      totalCost: '0.0012',
      latencyMs: 800,
      functionId: 'weather-lookup',
      metadata: {
        brandCode: 'ACME',
        operationType: 'function-call',
        userId: 'user-123',
        spanId: 'span-2',
        sessionId: 'session-abc',
      },
      systemPrompt: null,
      inputMessages: [{ role: 'function', content: 'get_weather(location="current")' }],
      outputContent: { weather: 'sunny', temperature: '72°F' },
    },
    {
      id: 'trace-3',
      parentSpanId: null,
      createdAt: '2024-01-14T11:15:00Z',
      modelName: 'claude-3',
      status: 'failed',
      inputTokens: 300,
      outputTokens: 0,
      totalCost: '0.0018',
      latencyMs: 2500,
      functionId: 'document-analysis',
      metadata: {
        brandCode: 'BETA',
        operationType: 'analysis',
        userId: 'user-456',
        spanId: 'span-3',
        sessionId: 'session-xyz',
      },
      systemPrompt: 'Analyze the following document for key insights.',
      inputMessages: [{ role: 'user', content: 'Please analyze this contract...' }],
      outputContent: null,
    },
  ];

  // Generate more mock data for pagination testing
  const mockTraces: LlmTraceData[] = [];
  for (let i = 0; i < 100; i++) {
    const baseTrace = baseTraces[i % baseTraces.length];
    const date = new Date(2024, 0, 15 - Math.floor(i / 10)); // Spread across different dates
    mockTraces.push({
      ...baseTrace,
      id: `trace-${i + 1}`,
      createdAt: date.toISOString(),
      modelName: ['gpt-4', 'gpt-3.5-turbo', 'claude-3', 'gemini-pro'][i % 4],
      status: ['completed', 'failed', 'pending'][i % 3],
      totalCost: (0.001 + i * 0.0001).toFixed(4),
      metadata: {
        ...baseTrace.metadata,
        brandCode: ['ACME', 'BETA', 'GAMMA', 'DELTA'][i % 4],
        userId: `user-${100 + (i % 50)}`,
        spanId: `span-${i + 1}`,
        sessionId: `session-${String.fromCharCode(
          97 + (i % 26),
        )}${String.fromCharCode(97 + ((i + 1) % 26))}${String.fromCharCode(97 + ((i + 2) % 26))}`,
      },
    });
  }
  return mockTraces;
};

export const generateMockEvaluations = (): LlmEvaluationData[] => {
  const baseEvaluations = [
    {
      id: 'eval-1',
      createdAt: '2024-01-15T10:35:00Z',
      evaluatorModel: 'gpt-4',
      status: 'completed',
      latencyMs: 2100,
      scores: {
        spanId: 'span-1',
        operationType: 'chat',
        brandCode: 'ACME',
        traceId: 'trace-1',
        functionId: 'chat-completion',
      },
      evaluationOutput: {
        overallScore: 8.5,
        accuracy: {
          score: 9,
          reasoning: 'Response was factually correct and relevant.',
        },
        completeness: {
          score: 8,
          reasoning: 'Covered most aspects but could be more detailed.',
        },
        instructionFollowing: {
          score: 9,
          reasoning: 'Followed instructions precisely.',
        },
        helpfulness: {
          score: 8,
          reasoning: 'Provided useful information to the user.',
        },
        improvements: [
          'Could provide more specific examples',
          'Consider adding follow-up questions',
        ],
        summary:
          'Good response overall with accurate information and proper instruction following.',
      },
      evaluationPrompt: 'Evaluate this AI response for accuracy, completeness, and helpfulness...',
    },
    {
      id: 'eval-2',
      createdAt: '2024-01-14T11:20:00Z',
      evaluatorModel: 'claude-3',
      status: 'completed',
      latencyMs: 1800,
      scores: {
        spanId: 'span-3',
        operationType: 'analysis',
        brandCode: 'BETA',
        traceId: 'trace-3',
        functionId: 'document-analysis',
      },
      evaluationOutput: {
        overallScore: 4.2,
        accuracy: {
          score: 3,
          reasoning: 'Analysis was incomplete due to processing failure.',
        },
        completeness: {
          score: 2,
          reasoning: 'Task was not completed successfully.',
        },
        instructionFollowing: {
          score: 7,
          reasoning: 'Attempted to follow instructions before failure.',
        },
        errorHandling: {
          score: 6,
          reasoning: 'Error was handled gracefully but not resolved.',
        },
        improvements: [
          'Implement better error recovery',
          'Add retry mechanisms for failed operations',
          'Provide more informative error messages',
        ],
        summary: 'Task failed to complete but error handling was adequate.',
      },
      evaluationPrompt: 'Evaluate this document analysis attempt, considering the failure mode...',
    },
  ];

  // Generate more mock data, ensuring evaluations reference actual trace IDs
  const mockEvaluations: LlmEvaluationData[] = [];
  for (let i = 0; i < 75; i++) {
    const baseEval = baseEvaluations[i % baseEvaluations.length];
    const date = new Date(2024, 0, 15 - Math.floor(i / 8)); // Spread across different dates
    const traceId = `trace-${(i % 50) + 1}`; // Reference actual traces (1-50)

    mockEvaluations.push({
      ...baseEval,
      id: `eval-${i + 1}`,
      createdAt: date.toISOString(),
      evaluatorModel: ['gpt-4', 'claude-3', 'gemini-pro'][i % 3],
      status: ['completed', 'failed', 'pending'][i % 3],
      latencyMs: i % 3 === 2 ? null : 1500 + ((i * 100) % 2000), // null for pending
      scores: {
        ...baseEval.scores,
        spanId: `span-${(i % 50) + 1}`,
        traceId: traceId,
        brandCode: ['ACME', 'BETA', 'GAMMA', 'DELTA'][i % 4],
        operationType: ['chat', 'analysis', 'summarization', 'translation'][i % 4],
        functionId: ['chat-completion', 'document-analysis', 'text-summary', 'translate'][i % 4],
      },
      evaluationOutput:
        i % 3 === 2
          ? null
          : {
              // null for pending
              ...baseEval.evaluationOutput!,
              overallScore: 3 + (i % 6),
            },
    });
  }
  return mockEvaluations;
};
